import React from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

export enum EditButtonStates {
  NOT_EDITING = 'NOT_EDITING',
  EDITING = 'EDITING',
  SAVING = 'SAVING',
}

export const DEFAULT_EDIT_BUTTON_STYLES = {
  cancelButton: ButtonStyle.TERTIARY_GRAY,
  doneButton: ButtonStyle.TERTIARY_BLUE,
  editButton: ButtonStyle.TERTIARY_GRAY,
  savingButton: ButtonStyle.TERTIARY_BLUE,
};

export interface ButtonStyles {
  cancelButton: ButtonStyle;
  doneButton: ButtonStyle;
  editButton: ButtonStyle;
  savingButton: ButtonStyle;
}

export interface EditButtonProps {
  buttonStyles?: ButtonStyles;
  editingState: EditButtonStates;
  saveDisabled?: boolean;
  editDisabled?: boolean;
  onCancel: () => void;
  onComplete: () => void;
  onEdit: () => void;
}

/*
 * In read mode, shows an Edit button that calls `onEdit` when clicked. If `editDisabled` is true, the Edit
 * button is disabled.
 * In edit mode, shows Cancel and Done buttons that call `onCancel` and `onComplete` respectively
 * when clicked. If `saveDisabled` is true, the Done button is disabled.
 * In saving mode, shows a disabled "Saving..." button.
 */
export default function EditButtons({
  buttonStyles = DEFAULT_EDIT_BUTTON_STYLES,
  editingState,
  saveDisabled = false,
  editDisabled = false,
  onCancel,
  onComplete,
  onEdit,
}: EditButtonProps): JSX.Element {
  switch (editingState) {
    case EditButtonStates.EDITING:
      return (
        <div>
          <Button
            buttonStyle={buttonStyles.cancelButton}
            onClick={onCancel}
            size={ButtonSize.MINI}
            text="Cancel"
          />
          <Button
            buttonStyle={buttonStyles.doneButton}
            className="u-mini-spacing-left"
            disabled={saveDisabled}
            onClick={onComplete}
            size={ButtonSize.MINI}
            text="Done"
            dataTestAttrs={{component: 'EditButtons', element: 'doneButton'}}
          />
        </div>
      );
    case EditButtonStates.NOT_EDITING:
      return (
        <Button
          buttonStyle={buttonStyles.editButton}
          disabled={editDisabled}
          onClick={onEdit}
          size={ButtonSize.MINI}
          text="Edit"
          dataTestAttrs={{component: 'EditButtons', element: 'editButton'}}
        />
      );
    case EditButtonStates.SAVING:
      return (
        <Button
          buttonStyle={buttonStyles.savingButton}
          disabled={true}
          size={ButtonSize.MINI}
          text="Saving..."
        />
      );
    default:
      throw new Error(`Invalid edit button state ${editingState}`);
  }
}
